// ProofTrackerPanel — Polish #18 proof-state tracker for the right rail.
//
// Renders the Zendesk-parity proof state machine (sent / awaiting / approved /
// rejected / superseded) for every proof row attached to this conversation.
// Earlier versions render behind a collapsible "Earlier versions" disclosure
// so the current version always owns the top of the panel.
//
// Kimi marks a proof by clicking "Mark as proof" on an outbound attachment
// inside the ThreadView — that's a sibling component, not this one. Here we
// just render the state, let her move it between states, and surface the
// "untrack" escape hatch for misclicks.
//
// The "Mark as proof" button comes from the ThreadView attachment bubble menu;
// we expose a helper callback via the `onMarkAttachment` prop that the parent
// page wires up.  Empty state: if this thread has no proofs, the panel renders
// a tiny "No proofs yet" caption — Kimi's ThreadView attachment menu still
// offers the "Mark as proof" action regardless.

import { useState } from 'react';
import {
  useProofs,
  useUpdateProof,
  useDeleteProof,
  type ProofRow,
} from '@/lib/hooks/useAttachments';

const STATUS_META: Record<
  ProofRow['status'],
  { label: string; dot: string; bg: string }
> = {
  sent:       { label: 'Sent',       dot: '#b3a27d', bg: '#f4ecd8' },
  awaiting:   { label: 'Awaiting',   dot: '#8795a5', bg: '#e8edf1' },
  approved:   { label: 'Approved',   dot: '#3f8a4d', bg: '#e1efde' },
  rejected:   { label: 'Rejected',   dot: '#b14e3b', bg: '#f5e0db' },
  superseded: { label: 'Superseded', dot: '#8a8579', bg: '#ece9e1' },
};

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes < 1024) return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface ProofTrackerPanelProps {
  conversationId: string | undefined;
}

export function ProofTrackerPanel({ conversationId }: ProofTrackerPanelProps) {
  const { data, isPending } = useProofs(conversationId);
  const updateProof = useUpdateProof(conversationId);
  const deleteProof = useDeleteProof(conversationId);
  const [earlierOpen, setEarlierOpen] = useState(false);

  if (!conversationId) return null;
  if (isPending) {
    return (
      <aside className="proof-panel proof-panel--pending">
        <div className="proof-panel-head">Proofs</div>
        <div className="proof-panel-skeleton" aria-hidden>
          <span />
          <span />
        </div>
      </aside>
    );
  }

  const proofs = data?.proofs ?? [];
  if (proofs.length === 0) {
    return (
      <aside className="proof-panel" aria-label="Proof tracker">
        <div className="proof-panel-head">Proofs</div>
        <div className="proof-panel-empty">
          No proofs yet. Mark an outbound attachment as a proof to track
          customer sign-off.
        </div>
      </aside>
    );
  }

  // Proofs come back version-desc from the backend.
  const [current, ...earlier] = proofs;
  const currentRow = current!;

  const renderProof = (p: ProofRow, isCurrent: boolean) => {
    const meta = STATUS_META[p.status];
    const busy = updateProof.isPending || deleteProof.isPending;
    // You can move between the four active states; `superseded` is structural
    // and gets set automatically when a later version lands.
    const moveable = p.status !== 'superseded';
    return (
      <div
        key={p.id}
        className={`proof-card${isCurrent ? ' proof-card--current' : ''}`}
      >
        <div className="proof-card-top">
          <span
            className="proof-status"
            style={{ background: meta.bg }}
          >
            <span
              className="proof-status-dot"
              style={{ background: meta.dot }}
              aria-hidden
            />
            {meta.label}
          </span>
          <span className="proof-version">v{p.version}</span>
          <span className="proof-timestamp">{formatDate(p.sent_at)}</span>
        </div>
        {p.attachment ? (
          <a
            href={p.attachment.url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="proof-attachment"
            onClick={(e) => {
              if (!p.attachment?.url) e.preventDefault();
            }}
          >
            <span aria-hidden>📎</span>
            <span className="proof-attachment-name">{p.attachment.filename}</span>
            <span className="proof-attachment-size">
              {formatBytes(p.attachment.size_bytes)}
            </span>
          </a>
        ) : (
          <div className="proof-attachment proof-attachment--missing">
            Attachment no longer available
          </div>
        )}
        {p.notes ? <div className="proof-notes">{p.notes}</div> : null}
        {moveable ? (
          <div className="proof-actions">
            {p.status !== 'approved' ? (
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() =>
                  updateProof.mutate({ proofId: p.id, status: 'approved' })
                }
                title="Customer approved this proof"
              >
                Approve
              </button>
            ) : null}
            {p.status !== 'rejected' ? (
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() =>
                  updateProof.mutate({ proofId: p.id, status: 'rejected' })
                }
                title="Customer asked for revisions"
              >
                Reject
              </button>
            ) : null}
            {p.status !== 'awaiting' ? (
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() =>
                  updateProof.mutate({ proofId: p.id, status: 'awaiting' })
                }
                title="Customer acknowledged, still reviewing"
              >
                Awaiting
              </button>
            ) : null}
            <button
              type="button"
              className="btn sm proof-untrack"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Untrack this proof? The file itself stays in the thread.')) {
                  deleteProof.mutate(p.id);
                }
              }}
              title="Remove this proof from the tracker (leaves the attachment in place)"
            >
              Untrack
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <aside className="proof-panel" aria-label="Proof tracker">
      <div className="proof-panel-head">
        Proofs
        <span className="proof-panel-count">
          {proofs.length} version{proofs.length === 1 ? '' : 's'}
        </span>
      </div>
      {renderProof(currentRow, true)}
      {earlier.length > 0 ? (
        <div className="proof-earlier">
          <button
            type="button"
            className="proof-earlier-toggle"
            onClick={() => setEarlierOpen((v) => !v)}
            aria-expanded={earlierOpen}
          >
            {earlierOpen ? 'Hide' : 'Show'} earlier version{earlier.length === 1 ? '' : 's'}
            <span className="proof-earlier-count">({earlier.length})</span>
          </button>
          {earlierOpen
            ? earlier.map((p) => renderProof(p, false))
            : null}
        </div>
      ) : null}
      {updateProof.isError ? (
        <div className="proof-error" role="alert">
          Couldn't update proof: {(updateProof.error as Error).message}
        </div>
      ) : null}
      {deleteProof.isError ? (
        <div className="proof-error" role="alert">
          Couldn't untrack proof: {(deleteProof.error as Error).message}
        </div>
      ) : null}
    </aside>
  );
}
